import React, { useState } from "react";
import {
  MdArrowBack,
  MdArrowForward,
  MdSync,
} from "react-icons/md";

import FilmStrip from "../../components/FilmStrip/FilmStrip";
import ComparisonViewer from "../../components/ComparisonViewer/ComparisonViewer";
import ZoomModal from "../../components/ZoomModal/ZoomModal";
import MobileZoomModal from "../../components/MobileZoomModal/MobileZoomModal";
import SearchBar from "../../components/SearchBar/SearchBar";
import { useVirtualTryOn } from "../../customHook/VirtualTryOn/useVirtualTryOn";
import "../../styles/comparisonViewer.css";

export default function VirtualTryOn() {
  const {
    activeGender,
    handleGenderChange,
    searchTerm,
    handleSearchChange,
    loading,
    error,
    hasAnyLooks,
    sectionsData,
    activeSlideIndices,
    pagination,
    mode,
    setMode,
    activeModeClass,
    inactiveModeClass,
    refMap,
    selectLook,
    previousBrowsePage,
    nextBrowsePage,
    sideCarouselState,
    scrollSideCarousel,
    updateSideCarouselState,
    zoomModal,
    openZoomModal,
    closeZoomModal,
  } = useVirtualTryOn();

  const [activeAiIndices, setActiveAiIndices] = useState({});

  return (
    <>
      <div className="app-page flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Title & Tab Bar */}
        <div className="px-4 sm:px-6 pt-3 sm:pt-4 pb-2 sm:pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3 lg:gap-4 shrink-0">
          <div className="shrink-0 min-w-0">
            <h2 className="font-headline-md text-[17px] sm:text-[20px] text-primary font-bold tracking-tight whitespace-nowrap">
              Virtual Try-On
            </h2>
            <p className="hidden lg:block font-body-md text-[10px] text-secondary mt-0.5">
              Explore catalog garment virtual try-on on user images paired for review.
            </p>
          </div>
          <div className="flex flex-row items-center justify-between sm:justify-end gap-2 sm:gap-2.5 lg:gap-3 w-full sm:w-auto min-w-0">
            {/* Gender selector tabs */}
            <div className="gender-tabs flex gap-0.5 bg-slate-100/80 p-0.5 rounded-md shrink-0">
              <button
                onClick={() => handleGenderChange("all")}
                className={`px-3 sm:px-4 py-1 rounded font-semibold text-xs transition-all duration-200 ${
                  !activeGender || activeGender === "all"
                    ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                    : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                } focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:outline-none active:ring-0 cursor-pointer`}
                type="button"
              >
                All
              </button>
              <button
                onClick={() => handleGenderChange("men")}
                className={`px-3 sm:px-4 py-1 rounded font-semibold text-xs transition-all duration-200 ${
                  activeGender === "men"
                    ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                    : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                } focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:outline-none active:ring-0 cursor-pointer`}
                type="button"
              >
                Men
              </button>
              <button
                onClick={() => handleGenderChange("women")}
                className={`px-3 sm:px-4 py-1 rounded font-semibold text-xs transition-all duration-200 ${
                  activeGender === "women"
                    ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                    : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                } focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:outline-none active:ring-0 cursor-pointer`}
                type="button"
              >
                Women
              </button>
            </div>

            <div className="showTellSearchWrap flex-1 sm:flex-initial min-w-0 flex justify-end">
              <SearchBar
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search"
              />
            </div>
          </div>
        </div>

        {/* Sections stage */}
        <section className="flex-1 flex flex-col min-h-0 relative">
          {loading ? (
            <div className="flex-1 flex flex-col items-center justify-center py-20 w-full text-center">
              <MdSync className="text-[36px] text-slate-400 animate-spin mb-2" />
              <p className="text-sm font-semibold text-secondary">Loading Virtual Try-On looks...</p>
            </div>
          ) : error ? (
            <div className="flex-grow flex flex-col items-center justify-center py-20 w-full text-center text-red-500">
              <p className="text-sm font-semibold">Error: {error}</p>
            </div>
          ) : !hasAnyLooks ? (
            <div className="flex-1 flex flex-col items-center justify-center py-20 w-full text-center">
              <p className="text-sm font-semibold text-secondary">No looks found.</p>
            </div>
          ) : (
            sectionsData.map((section) => {
              const visibleLooks = section.slides || [];
              const isVisible =
                (!activeGender || activeGender === "all" || section.activeKey === activeGender) &&
                visibleLooks.length > 0;
              const activeIndex = activeSlideIndices[section.activeKey] ?? 0;
              const totalSlides = pagination.total || visibleLooks.length;
              const activeSlide = visibleLooks[activeIndex] || {};
              const rangeStart =
                totalSlides === 0
                  ? 0
                  : (pagination.page - 1) * pagination.limit + 1;
              const rangeEnd =
                totalSlides === 0
                  ? 0
                  : Math.min(rangeStart + visibleLooks.length - 1, totalSlides);

              const slideKey =
                activeSlide.id ??
                activeSlide.iProductId ??
                `${section.activeKey}-${activeIndex}`;
              const currentAiData = activeAiIndices[slideKey] || {
                current: 1,
                total: activeSlide.slides?.length || activeSlide.count || 1,
              };

            return (
              <div
                key={section.id}
                className={
                  isVisible
                    ? "flex flex-col flex-1 min-h-0 overflow-hidden"
                    : "hidden"
                }
              >
                {/* Visual stage view */}
                <div className="showtell-stage flex-1 relative px-3 sm:px-4 lg:px-6 py-2 min-h-0 flex flex-col justify-center overflow-hidden">
                  {/* Prev look arrow */}
                  <button
                    ref={refMap[section.prevRef]}
                    className="app-icon-button hidden lg:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full items-center justify-center cursor-pointer"
                  >
                    <MdArrowBack size={20} />
                  </button>

                  {/* Next look arrow */}
                  <button
                    ref={refMap[section.nextRef]}
                    className="app-icon-button hidden lg:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full items-center justify-center cursor-pointer"
                  >
                    <MdArrowForward size={20} />
                  </button>

                  {/* Content grid */}
                  <div className="showtell-stage-grid h-full max-w-[1240px] w-full mx-auto grid grid-cols-1 lg:grid-cols-[230px_1fr] gap-3 lg:gap-6 items-center justify-between px-0 lg:px-6 min-h-0">
                    {/* Left details panel */}
                    <aside className="app-card justify-self-center lg:justify-self-start flex flex-col sm:flex-row lg:flex-col sm:items-center lg:items-start justify-between gap-2 lg:gap-4 p-2 sm:px-4 sm:py-2 lg:p-3 w-full max-w-[390px] sm:max-w-[720px] lg:w-[230px] lg:max-w-none shrink-0">
                      {/* Mobile & Tablet view: category, title & counter unified in 1 row */}
                      <div className="lg:hidden w-full min-w-0 sm:flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0 flex-1">
                            {activeSlide.category && (
                              <span className="text-[8px] sm:text-[9px] text-secondary uppercase tracking-wider font-bold shrink-0">
                                {activeSlide.category.toUpperCase()} •
                              </span>
                            )}
                            <h3
                              id="look-title"
                              className="font-headline-lg text-[13px] sm:text-[14px] leading-snug text-primary font-bold truncate min-w-0"
                              title={activeSlide.productTitle || activeSlide.productName || activeSlide.title || activeSlide.originalAlt || section.title}
                            >
                              {activeSlide.productTitle || activeSlide.productName || activeSlide.title || activeSlide.originalAlt || section.title}
                            </h3>
                          </div>
                          <span
                            id="look-count"
                            className="inline-block text-[9px] text-secondary px-1.5 py-0.5 bg-surface-container rounded font-semibold whitespace-nowrap shrink-0"
                          >
                            {currentAiData.current} / {currentAiData.total}
                          </span>
                        </div>
                      </div>

                      {/* Desktop view: multi-line vertical sidebar layout */}
                      <div className="hidden lg:block min-w-0 w-full">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <p
                            id="look-meta"
                            className="text-[10px] text-secondary uppercase tracking-widest font-bold leading-3 truncate"
                          >
                            {activeSlide.category ? activeSlide.category.toUpperCase() : "LOOK"}
                          </p>
                          <span
                            id="look-count"
                            className="inline-block text-[10px] text-secondary px-1.5 py-0.5 bg-surface-container rounded font-semibold whitespace-nowrap shrink-0"
                          >
                            {currentAiData.current} / {currentAiData.total}
                          </span>
                        </div>
                        <h3
                          id="look-title"
                          className="font-headline-lg text-[18px] leading-snug text-primary font-bold line-clamp-2"
                          title={activeSlide.productTitle || activeSlide.productName || activeSlide.title || activeSlide.originalAlt || section.title}
                        >
                          {activeSlide.productTitle || activeSlide.productName || activeSlide.title || activeSlide.originalAlt || section.title}
                        </h3>
                        <p className="text-[11px] text-secondary mt-1.5 leading-4">
                          {activeSlide.category ? `${activeSlide.category} • ` : ""}
                          {activeSlide.genderName ? `${activeSlide.genderName} • ` : ""}
                          {activeSlide.slides?.length || activeSlide.count || 1} AI generation{(activeSlide.slides?.length || activeSlide.count || 1) > 1 ? "s" : ""}
                        </p>
                      </div>
                      <div className="inline-flex bg-slate-100/80 p-0.5 lg:p-1 rounded-md lg:rounded-lg w-full sm:w-auto lg:w-fit border border-slate-200/80 whitespace-nowrap shrink-0">
                        <button
                          onClick={() => setMode("side")}
                          className={`${mode === "side" ? activeModeClass : inactiveModeClass} flex-1 sm:flex-none px-3 py-1`}
                        >
                          Side by Side
                        </button>
                        <button
                          onClick={() => setMode("compare")}
                          className={`${mode === "compare" ? activeModeClass : inactiveModeClass} flex-1 sm:flex-none px-3 py-1`}
                        >
                          Compare
                        </button>
                      </div>
                    </aside>

                    {/* Central Image Compare/Slider Area */}
                    <div
                      id="view-container"
                      className="min-w-0 flex gap-4 h-full items-center justify-center overflow-hidden w-full"
                    >
                      <div
                        className={`swiper ${section.id}_swiper w-full h-full flex items-center justify-center`}
                        ref={refMap[section.swiperRef]}
                      >
                        <div className="swiper-wrapper">
                          {section.slides.map((slide, idx) => {
                            const isFirstSlide = idx === 0;
                            const isAdjacentSlide =
                              idx === activeIndex ||
                              idx === activeIndex + 1 ||
                              idx === activeIndex - 1;
                            const shouldPreload =
                              isFirstSlide || isAdjacentSlide;
                            const transformedPreviewImage =
                              slide.slides?.[0]?.preview ||
                              slide.slides?.[0]?.transformed ||
                              slide.preview ||
                              slide.transformed;
                            const transformedPreviewAlt =
                              slide.slides?.[0]?.transformedAlt ||
                              slide.transformedAlt ||
                              slide.productTitle ||
                              slide.title;
                            const sideCarouselKey = `${section.activeKey}-${idx}`;

                            return (
                              <div
                                key={slide.iProductId || idx}
                                className="swiper-slide flex items-center justify-center"
                              >
                                <ComparisonViewer
                                  mode={mode}
                                  originalSrc={slide.original}
                                  originalAlt={slide.productTitle || slide.productName || slide.title || slide.originalAlt}
                                  transformedSrc={transformedPreviewImage}
                                  transformedAlt={transformedPreviewAlt}
                                  slides={slide.slides || []}
                                  shouldPreload={shouldPreload}
                                  onOpenZoomOriginal={() =>
                                    openZoomModal(slide.original, null, false)
                                  }
                                  onOpenZoomTransformed={(src) =>
                                    openZoomModal(src || transformedPreviewImage, null, false)
                                  }
                                  sideCarouselKey={sideCarouselKey}
                                  sideCarouselState={sideCarouselState}
                                  scrollSideCarousel={scrollSideCarousel}
                                  updateSideCarouselState={updateSideCarouselState}
                                  onActiveIndexChange={
                                    idx === activeIndex
                                      ? (currIndex, totalCount) => {
                                          setActiveAiIndices((prev) => ({
                                            ...prev,
                                            [slideKey]: {
                                              current: currIndex + 1,
                                              total: totalCount,
                                            },
                                          }));
                                        }
                                      : undefined
                                  }
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <FilmStrip
                  rangeStart={rangeStart}
                  rangeEnd={rangeEnd}
                  totalSlides={totalSlides}
                  previousBrowsePage={previousBrowsePage}
                  nextBrowsePage={nextBrowsePage}
                  visibleLooks={visibleLooks}
                  activeIndex={activeIndex}
                  selectLook={selectLook}
                  section={section}
                  currentPage={pagination.page || 1}
                  totalPages={pagination.totalPages || 1}
                />
              </div>
            );
          }))}
        </section>
      </div>

      <ZoomModal
        modalId="modelZoomModal"
        front={zoomModal.front}
        back={zoomModal.back}
        onClose={closeZoomModal}
        showFlip={zoomModal.showFlip}
      />
      <MobileZoomModal
        isOpen={zoomModal.isMobileOpen}
        front={zoomModal.front}
        back={zoomModal.back}
        onClose={closeZoomModal}
        showFlip={zoomModal.showFlip}
      />
    </>
  );
}
